import React, {
    useState, useMemo, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { Tab, Nav } from 'react-bootstrap';
import { withStyles, fade } from '@material-ui/core/styles';
import clsx from 'clsx';
import {
    Tooltip, Typography, Button, Box,
} from '@material-ui/core';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import BorderColorOutlinedIcon from '@material-ui/icons/BorderColorOutlined';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import DealStyles from 'styles/modules/DealStyles';
import TabStyles from 'styles/widgets/tabs/TabsStyles';
import DealBackend from 'components/modules/deals/read/dealTab/DealBackend';
import DealCPITabContent from 'components/modules/deals/read/dealTab/DealCPITabContent';
import {
    DealType, DealSection, DealTabsTitle, PaymentFrequency,
} from 'utils/enum/DealEnum';
import { MEDIA_QUERY } from 'utils/enum/Core';
import GraphQLClient from 'services/apollo/GraphQLClient';
import DealsMutate from 'services/graphQL/mutate/DealsMutate';
import DateUtils from 'lib/DateUtils';
import ModalUtils from 'utils/ModalUtils';
import { isValidSchema } from 'utils/schema/utils';
import { CPISchema } from 'utils/schema/deal/CPI';

const tabStyles = (theme) => TabStyles.tabPanel(theme);

const styles = (theme) => ({
    ...DealStyles.dealStyles(theme, fade),
    ...tabStyles(theme),
    rootContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
    },
    flexContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
    },
    cpiTooltipSpan: {
        pointerEvents: 'auto',
    },
    navbar: {
        '& > a': {
            color: theme.palette.text.link,
            'text-decoration': 'none',
            'background-color': 'transparent',
            'line-height': '1.5',
        },
        '@media (max-width: 959px)': {
            display: 'flex',
        },
    },
    tabsContainer: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        '& .tab-content': {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
        },
        '& .tab-pane': {
            height: '100%',
        },
    },
    cpiContainer: {
        padding: theme.spacing(2),
        '& .MuiFormControlLabel-label': {
            color: theme.palette.text.primary,
        },
    },
    cpiTabPlaceholder: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '120px',
        padding: theme.spacing(2),
    },
    cpiRow: {
        marginBottom: theme.spacing(1.5),
    },
    cpiColumnStack: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        backgroundColor: '#f8fafc',
        width: '100%',
        alignSelf: 'stretch',
        boxSizing: 'border-box',
        padding: theme.spacing(1.5),
    },
    cpiColumnStackPlain: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        width: '100%',
        alignSelf: 'stretch',
        boxSizing: 'border-box',
        padding: theme.spacing(1.5),
    },
    cpiLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.grayLabel,
        marginBottom: 0,
    },
    cpiReadOnlyLabel: {
        minWidth: '120px',
        [theme.breakpoints.down('xs')]: {
            minWidth: 'auto',
        },
    },
    cpiField: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        '& .select-bootstrap': {
            flex: 1,
            minWidth: 0,
            width: '100%',
        },
        '& .react-datepicker-wrapper': {
            width: '100%',
        },
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            '& label': {
                minWidth: 'auto',
            },
            '& > *': {
                width: '100%',
            },
        },
    },
    cpiDescriptionField: {
        minHeight: 45,
    },
    cpiFieldEdit: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(0.5),
        '& .select-bootstrap': {
            width: '100%',
        },
        '& .react-datepicker-wrapper': {
            width: '100%',
        },
        '& label': {
            marginBottom: 0,
            fontSize: 14,
            fontWeight: 500,
        },
        '& > *': {
            width: '100%',
        },
    },
    cpiCheckbox: {
        marginBottom: 0,
    },
    cpiCheckboxRow: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 80,
    },
    cpiDescriptionFieldEdit: {
        minHeight: 80,
    },
    cpiSelect: {
        width: '100%',
    },
    cpiReadOnlyValue: {
        flex: 1,
        minHeight: 31,
        display: 'flex',
        alignItems: 'center',
    },
    cpiReadOnlyField: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1.5),
        '& h6': {
            minWidth: '220px',
        },
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            '& h6': {
                minWidth: 'auto',
            },
        },
    },
    backendActionsContainer: {
        gap: '8px',
        position: 'absolute',
        right: '16px',
        top: '8px',
        alignItems: 'center',
        zIndex: 10,
    },
    cpiActionsContainer: {
        gap: '8px',
        position: 'absolute',
        right: '16px',
        top: '8px',
        alignItems: 'center',
        zIndex: 10,
    },
});

const DealBackendCPITabs = ({
    classes,
    canReadBackEnd,
    clientId,
    accountNumber,
    editingMode,
    editingStatus,
    allowEditDeal,
    callDealPreview,
    onCancelProduct,
    postedDate,
    onChangeEditingMode,
    dealType,
    dealStructure,
    cpiData,
}) => {
    const graphqlClient = useMemo(() => new GraphQLClient(), []);
    const emptyCPIRecord = {
        description: '',
        collectFirstCPIPaymentAtSigning: false,
        amountDueAtSigning: 0,
        frequency: null,
        firstDueDate: null,
        amount: null,
        lastDueDate: null,
        active: false,
    };
    const [activeTab, setActiveTab] = useState(0);
    const [cpiEditingMode, setCPIEditingMode] = useState(false);
    const [isSavingCPI, setIsSavingCPI] = useState(false);
    const [isDeletingCPI, setIsDeletingCPI] = useState(false);
    const [cpiRecord, setCPIRecord] = useState(emptyCPIRecord);
    const [cpiDraft, setCPIDraft] = useState(emptyCPIRecord);
    const { isEditing } = editingStatus;
    const {
        collectFirstCPIPaymentAtSigning,
        amountDueAtSigning,
    } = cpiDraft;

    const { isValid: isCPIFormValid, errors: cpiValidationErrors } = isValidSchema(CPISchema, {
        description: cpiDraft.description,
        collectFirstCPIPaymentAtSigning: !!cpiDraft.collectFirstCPIPaymentAtSigning,
        amountDueAtSigning: cpiDraft.amountDueAtSigning,
        frequency: cpiDraft.frequency,
        firstDueDate: cpiDraft.firstDueDate,
        amount: cpiDraft.amount,
        lastDueDate: cpiDraft.lastDueDate,
    });

    const {
        collectFirstCPIPaymentAtSigning: dealStructureCollectFirst,
        amountDueAtSigning: dealStructureAmountDue,
        paymentFrequency: dealStructureFrequency,
        firstPaymentDue: dealStructureFirstDueDate,
        finalPaymentDue: dealStructureLastDueDate,
    } = dealStructure || {};

    useEffect(() => {
        if (cpiEditingMode && !collectFirstCPIPaymentAtSigning) {
            if (amountDueAtSigning !== 0 && amountDueAtSigning !== null) {
                setCPIDraft((prev) => ({ ...prev, amountDueAtSigning: 0 }));
            }
        }
    }, [cpiEditingMode, collectFirstCPIPaymentAtSigning, amountDueAtSigning]);

    const isCPITabEnabled = dealType === DealType.BHPH || dealType === DealType.LHPH;
    const isAnyoneEditing = editingMode.isEditing || isEditing;
    const isBackendEditDisabled = isAnyoneEditing && editingMode.sectionName !== DealSection.BACK_END;
    const isCPIEditDisabled = isAnyoneEditing && editingMode.sectionName !== DealSection.CPI;
    const isEditDisabled = isBackendEditDisabled;
    const hasActiveCPI = !!cpiRecord.active;
    const hasChanges = useMemo(() => {
        if (!hasActiveCPI) return true;

        const getTime = (date) => (date ? new Date(date).getTime() : null);

        return (
            cpiDraft.description !== cpiRecord.description
            || !!cpiDraft.collectFirstCPIPaymentAtSigning !== !!cpiRecord.collectFirstCPIPaymentAtSigning
            || cpiDraft.amountDueAtSigning !== cpiRecord.amountDueAtSigning
            || cpiDraft.frequency !== cpiRecord.frequency
            || cpiDraft.amount !== cpiRecord.amount
            || getTime(cpiDraft.firstDueDate) !== getTime(cpiRecord.firstDueDate)
            || getTime(cpiDraft.lastDueDate) !== getTime(cpiRecord.lastDueDate)
        );
    }, [cpiDraft, cpiRecord, hasActiveCPI]);
    const cpiDisabledTooltip = 'CPI configuration is only available for BHPH and LHPH deal types.';
    const backendActionsContainerId = `deal-backend-actions-${accountNumber}`;
    const cpiActionsContainerId = `deal-cpi-actions-${accountNumber}`;

    const mapDateValue = (value) => {
        if (!value) return null;

        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [year, month, day] = value.split('-').map(Number);
            return new Date(year, month - 1, day);
        }

        const normalized = DateUtils.getOnlyDate(value);
        if (normalized) return new Date(normalized);

        const fallbackDate = new Date(value);
        return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
    };

    useEffect(() => {
        const cpiDataActive = !!cpiData?.active;

        const nextRecord = {
            description: cpiData?.description || '',
            collectFirstCPIPaymentAtSigning: !!dealStructureCollectFirst,
            amountDueAtSigning: dealStructureAmountDue ?? 0,
            frequency: cpiDataActive ? cpiData?.frequency : null,
            firstDueDate: cpiDataActive ? mapDateValue(cpiData?.firstDueDate) : null,
            amount: cpiData?.amount ?? null,
            lastDueDate: cpiDataActive ? mapDateValue(cpiData?.lastDueDate) : null,
            active: cpiDataActive,
        };

        setCPIRecord(nextRecord);

        if (!cpiEditingMode) {
            setCPIDraft(nextRecord);
        }
    }, [
        cpiData,
        cpiEditingMode,
        dealStructureCollectFirst,
        dealStructureAmountDue,
    ]);

    const onSelectTab = (key) => {
        const tab = Number(key);
        if (tab === 1 && !isCPITabEnabled) return;
        setActiveTab(tab);
    };

    const handleCPIEdit = () => {
        onChangeEditingMode(true, DealSection.CPI, DealTabsTitle.DEAL);
        setCPIDraft(hasActiveCPI ? cpiRecord : {
            ...cpiRecord,
            frequency: dealStructureFrequency ? dealStructureFrequency.toUpperCase() : null,
            firstDueDate: mapDateValue(dealStructureFirstDueDate),
            lastDueDate: mapDateValue(dealStructureLastDueDate),
        });
        setCPIEditingMode(true);
    };

    const handleCPISave = async () => {
        if (isSavingCPI) return;
        if (!isCPIFormValid) return;

        const draftFirstDueDate = cpiDraft.firstDueDate ?? dealStructureFirstDueDate;
        const draftLastDueDate = cpiDraft.lastDueDate ?? dealStructureLastDueDate;

        const input = {
            description: cpiDraft.description || '',
            collectFirstCPIPaymentAtSigning: !!cpiDraft.collectFirstCPIPaymentAtSigning,
            amountDueAtSigning: cpiDraft.amountDueAtSigning,
            frequency: cpiDraft.frequency || dealStructureFrequency || PaymentFrequency.MONTHLY,
            firstDueDate: draftFirstDueDate ? DateUtils.getOnlyDate(draftFirstDueDate) : null,
            amount: cpiDraft.amount,
            lastDueDate: draftLastDueDate ? DateUtils.getOnlyDate(draftLastDueDate) : null,
        };

        try {
            setIsSavingCPI(true);
            const response = await graphqlClient.mutate(DealsMutate.SAVE_DEAL_CPI, { accountNumber, input });
            const { graphQLErrors, data } = response || {};

            if (graphQLErrors) {
                ModalUtils.errorMessage(graphQLErrors);
                return;
            }

            if (!data?.saveDealCPI) {
                ModalUtils.errorMessage([], 'Unable to save CPI. Please review the values and try again.');
                return;
            }

            setCPIRecord({ ...cpiDraft, active: true });
            onChangeEditingMode();
            setCPIEditingMode(false);
        } catch (error) {
            ModalUtils.errorMessage(null, error);
        } finally {
            setIsSavingCPI(false);
        }
    };

    const handleCPIDelete = async () => {
        if (isDeletingCPI || !hasActiveCPI) return;

        try {
            setIsDeletingCPI(true);
            const response = await graphqlClient.mutate(DealsMutate.DELETE_DEAL_CPI, { accountNumber });
            const { graphQLErrors, data } = response || {};

            if (graphQLErrors) {
                ModalUtils.errorMessage(graphQLErrors);
                return;
            }

            if (!data?.deleteDealCPI) {
                ModalUtils.errorMessage([], 'Unable to delete CPI. Please try again.');
                return;
            }

            setCPIRecord(emptyCPIRecord);
            setCPIDraft(emptyCPIRecord);
        } catch (error) {
            ModalUtils.errorMessage(null, error);
        } finally {
            setIsDeletingCPI(false);
        }
    };

    const handleCPICancel = () => {
        onChangeEditingMode();
        setCPIDraft(cpiRecord);
        setCPIEditingMode(false);
    };

    const onChangeCPIDraft = (field, value) => {
        setCPIDraft((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <Box className={classes.rootContainer}>
            <div className={classes.flexContainer}>
                <Tab.Container activeKey={activeTab} onSelect={onSelectTab} mountOnEnter={false}>
                    <div className={classes.tabsContainer}>
                        <Nav variant="tabs" className={classes.navbar}>
                            <Nav.Item>
                                <Nav.Link eventKey={0} className={classes.tab}>
                                    Back End
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey={1} disabled={!isCPITabEnabled} className={classes.tab}>
                                    <Tooltip
                                        title={!isCPITabEnabled ? cpiDisabledTooltip : ''}
                                        disableHoverListener={isCPITabEnabled}
                                        placement="bottom-start"
                                    >
                                        <span className={classes.cpiTooltipSpan}>CPI</span>
                                    </Tooltip>
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey={0} className={clsx(classes.tabPane)}>
                                {canReadBackEnd ? (
                                    <DealBackend
                                        clientId={clientId}
                                        dealId={accountNumber}
                                        editingMode={editingMode}
                                        editingStatus={editingStatus}
                                        isEditDisabled={isEditDisabled}
                                        allowEditDeal={allowEditDeal}
                                        callDealPreview={callDealPreview}
                                        onCancelProduct={onCancelProduct}
                                        postedDate={postedDate}
                                        onChangeEditingMode={onChangeEditingMode}
                                        externalActionsContainerId={activeTab === 0 ? backendActionsContainerId : null}
                                    />
                                ) : (
                                    <div className={classes.cpiTabPlaceholder}>
                                        <Typography variant="body2">Insufficient permissions to view Back End.</Typography>
                                    </div>
                                )}
                            </Tab.Pane>
                            <Tab.Pane eventKey={1} className={clsx(classes.tabPane)}>
                                <DealCPITabContent
                                    classes={classes}
                                    cpiEditingMode={cpiEditingMode}
                                    cpiRecord={cpiRecord}
                                    cpiDraft={cpiDraft}
                                    onChangeCPIDraft={onChangeCPIDraft}
                                    dealStructure={dealStructure}
                                    cpiValidationErrors={cpiValidationErrors}
                                />
                            </Tab.Pane>
                        </Tab.Content>
                    </div>
                </Tab.Container>
            </div>
            <Box
                id={backendActionsContainerId}
                className={classes.backendActionsContainer}
                style={{
                    display: activeTab === 0 ? 'flex' : 'none',
                }}
            />
            <Box
                id={cpiActionsContainerId}
                className={classes.cpiActionsContainer}
                style={{
                    display: activeTab === 1 ? 'flex' : 'none',
                }}
            >
                {cpiEditingMode ? (
                    <div>
                        <Button
                            className={classes.textSuccess}
                            size="small"
                            startIcon={<SaveOutlinedIcon />}
                            onClick={handleCPISave}
                            disabled={isSavingCPI || !isCPIFormValid || !hasChanges}
                        >
                            Save
                        </Button>
                        <Button
                            size="small"
                            onClick={handleCPICancel}
                            className={classes.textError}
                            startIcon={<HighlightOffIcon />}
                            disabled={isSavingCPI}
                        >
                            Cancel
                        </Button>
                    </div>
                ) : (
                    allowEditDeal && (
                        <div>
                            {hasActiveCPI && (
                                <Button
                                    size="small"
                                    onClick={handleCPIDelete}
                                    className={classes.textError}
                                    startIcon={<DeleteOutlineOutlinedIcon />}
                                    disabled={isCPIEditDisabled || isDeletingCPI}
                                >
                                    Delete
                                </Button>
                            )}
                            <Button
                                color="primary"
                                size="small"
                                disabled={isCPIEditDisabled || isDeletingCPI}
                                onClick={handleCPIEdit}
                                startIcon={<BorderColorOutlinedIcon />}
                            >
                                Edit
                            </Button>
                        </div>
                    )
                )}
            </Box>
        </Box>
    );
};

DealBackendCPITabs.propTypes = {
    classes: PropTypes.oneOfType([PropTypes.object]).isRequired,
    canReadBackEnd: PropTypes.bool.isRequired,
    clientId: PropTypes.string.isRequired,
    accountNumber: PropTypes.number.isRequired,
    editingMode: PropTypes.shape({
        isEditing: PropTypes.bool,
        sectionName: PropTypes.string,
    }).isRequired,
    editingStatus: PropTypes.shape({
        isEditing: PropTypes.bool,
        editingById: PropTypes.number,
        editingBy: PropTypes.string,
    }).isRequired,
    allowEditDeal: PropTypes.bool.isRequired,
    callDealPreview: PropTypes.func.isRequired,
    onCancelProduct: PropTypes.func.isRequired,
    postedDate: PropTypes.string,
    onChangeEditingMode: PropTypes.func.isRequired,
    dealType: PropTypes.string,
    dealStructure: PropTypes.shape({
        collectFirstCPIPaymentAtSigning: PropTypes.bool,
        amountDueAtSigning: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        paymentFrequency: PropTypes.string,
        firstPaymentDue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        finalPaymentDue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }),
    cpiData: PropTypes.shape({
        cpiId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        description: PropTypes.string,
        frequency: PropTypes.string,
        firstDueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lastDueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        active: PropTypes.bool,
    }),
};

DealBackendCPITabs.defaultProps = {
    postedDate: null,
    dealType: null,
    dealStructure: null,
    cpiData: null,
};

export default withStyles(styles)(DealBackendCPITabs);
